import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  styled,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { BLACK, GRAY_LIGHT, GRAY_LIGHT_RGB_BORDER, GRAY_MAIN, GREEN_LOGO, GREEN_MAIN, WHITE } from '../../theme/colors';
import { useTranslationContext } from '../../hooks/translations/translationsProvider';
import settings from '../../utilities/settings';
import { useWaiter } from '../../hooks/waiter/WaiterProvider';
import { delay } from '../../utilities/delay';
import { dashboard } from '../../hooks/actions/dashboard';
import CheckBoxTermsAndConditions from '../CheckBoxTermsAndConditions';

import generateRandomString from '../../helpers/randomString';
import { IRedeemRequestModel } from '../../models/redeem';
import TextFieldCustom from '../TextField/TextFieldCustom';
import TextFieldWithErrorPropsCustom from '../TextField/TextFieldWithErrorPropsCustom';
import IbanForm from './IbanForm';
import { redeemRequest } from '../../hooks/actions/redeem';

const FormCustom = styled('form')(({theme})=>({
    width:'100%',                                      
    [theme.breakpoints.up('md')]: {
        width:'80%',
    },
}))

const ResponseBox = styled(Box)(({theme})=>({
    width:'100%',
    marginTop:"30px",                                      
    [theme.breakpoints.up('md')]: {
        width:'80%',
    },
}))

interface RedeemFormProps {
    setStatus: (status: boolean) => void;
    status: boolean;
};

const RedeemForm: React.FC<RedeemFormProps> = ({setStatus, status}) => {
    const [response, setResponse] = useState(null);
    const {t} = useTranslationContext();

    const [amountAvaiable, setAmountAvaible] = useState<number>(0);

    const [amount, setAmount] = useState('');
    const [errorAmount, setErrorAmount] = useState(false);
    const [errorAmountMessage, setErrorAmountMessage] = useState('')

    const [iban, setIban] = useState('');
    const [errorIban, setErrorIban] = useState(false);

    const [bic, setBic] = useState('');
    const [errorBic, setErrorBic] = useState(false);

    const [receiverName, setReceiverName] = useState('');
    const [errorReceiverName, setErrorReceiverName] = useState(false);

    const [isSended, setIsSended] = useState(false);
    const [mainErrorMex, setMainErrorMex]=useState<string | null>(null)
    const [mainResponseMex, setMainResponseMex]=useState<string | null>(null)

    const {setWaiterView} = useWaiter();
    const [isChecked, setIsChecked] = useState(false); 

    const minAmountInEuro:number = settings.RedeemTokens.MinAmountInEuro;
    const maxAmountInEuro:number = settings.RedeemTokens.MaxAmountInEuro;
    const fixedFeeInEuro:number = settings.RedeemTokens.FixedFee;
    const percentFee:number = settings.RedeemTokens.PercentFee;
    const divisa:string = settings.RedeemTokens.Divisa;
    const tokenForFiat:number = settings.RedeemTokens.TokenForFiat;
    const decimals: number = settings.Token.Decimals;

    const minAmountInTokens:string = (minAmountInEuro * tokenForFiat).toFixed(2);
    const maxAmountInTokens:string = (maxAmountInEuro * tokenForFiat).toFixed(2);

    const [percentFeeInEuro, setPercentFeeInEuro]= useState('');
    const [NetAmountInEuro, setNetAmountInEuro] = useState('');
    const [amountInEuro, setAmountInEuro] = useState('');
    const decimalEuro = 2;
    const decimalAsso = decimals;

    useEffect(()=>{
        setWaiterView(true)
        delay(500).finally(()=>{
            dashboard()
                .then(resp => {
                    setAmountAvaible(resp.balance)
                })
                .catch(error => {
                    // initLoad.current = false
                    console.log(error)
                })
            setWaiterView(false)
        })
    },[])

    const handleInputAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setErrorAmount(false);
        setErrorAmountMessage('');
        const value = e.target.value;
        const regex = /^\d*\.?\d{0,2}$/;

        if(value === ''){
            setAmount('');
            setAmountInEuro('');
            setNetAmountInEuro('');
            setPercentFeeInEuro('');
            setErrorAmount(false);
            return;
        }

        if (value.includes('.')) {
            const [integerPart, decimalPart] = value.split('.');
            if (decimalPart.length > decimalAsso) {
                setErrorAmount(false);
                return;
            }
        }

        if(!regex.test(value)){
            setErrorAmountMessage(t("Inserisci un importo valido"))
            setErrorAmount(true)
        }

        if (parseFloat(value) > 0) {
            if(parseFloat(value) > amountAvaiable){
                setAmount(amountAvaiable.toFixed(decimalAsso))
                setAmountInEuro((amountAvaiable / tokenForFiat).toFixed(decimalEuro)); // usato il decimal 2 fisso perchè è euro e ha fisso 2 decimali

                const percInE:number = amountAvaiable / tokenForFiat * percentFee / 100
                setPercentFeeInEuro(percInE.toFixed(decimalEuro));
                const netAmInE: number = (amountAvaiable / tokenForFiat) - percInE - fixedFeeInEuro
                setNetAmountInEuro(netAmInE.toFixed(decimalEuro))
                return;
            }
            if(parseFloat(value) > parseFloat(maxAmountInTokens)){
                setAmount(parseFloat(maxAmountInTokens).toFixed(decimalAsso));
                setAmountInEuro((amountAvaiable / tokenForFiat).toFixed(decimalEuro));

                const percInE:number = parseFloat(maxAmountInTokens) / tokenForFiat * percentFee / 100
                setPercentFeeInEuro(percInE.toFixed(decimalEuro));    
                const netAmInE: number = (parseFloat(maxAmountInTokens) / tokenForFiat) - percInE - fixedFeeInEuro
                setNetAmountInEuro(netAmInE.toFixed(decimalEuro))
                return;
            }
            setAmount(value);
            setAmountInEuro((amountAvaiable / tokenForFiat).toFixed(decimalEuro));

            const percInE:number = parseFloat(value) / tokenForFiat * percentFee / 100
            setPercentFeeInEuro(percInE.toFixed(decimalEuro));
            const netAmInE: number = (parseFloat(value) / tokenForFiat) - percInE - fixedFeeInEuro
            setNetAmountInEuro(netAmInE.toFixed(decimalEuro))
            setErrorAmount(false);
        } 
    };

    const handleBlurAmount = () => {
        if (!errorAmount && amount) {
            // se amount minore di minimoAmount
            if(parseFloat(amount) < parseFloat(minAmountInTokens)){
                setAmount(minAmountInTokens);
                setAmountInEuro((parseFloat(minAmountInTokens) / tokenForFiat).toFixed(decimalEuro));
                return;
            }

            // se amount è maggiore maxAountin token
            if(parseFloat(amount) > parseFloat(maxAmountInTokens)){
                // se maxAmount minore di amount disponibile allora setta maxAmount
                // else setta amountdisponibile
                if(parseFloat(maxAmountInTokens) < amountAvaiable){
                    setAmount(maxAmountInTokens);
                    setAmountInEuro((parseFloat(maxAmountInTokens) / tokenForFiat).toFixed(decimalEuro))
                }else{
                    setAmount(amountAvaiable.toFixed(decimalAsso));
                    setAmountInEuro((amountAvaiable / tokenForFiat).toFixed(decimalEuro));
                }
                return;
            }
            const newAmount = parseFloat(amount).toFixed(settings.Token.Decimals)
            setAmount(newAmount);
            setAmountInEuro((parseFloat(amount) / tokenForFiat).toFixed(decimalEuro));
            const percInE:number = parseFloat(newAmount) / tokenForFiat * percentFee / 100
            setPercentFeeInEuro(percInE.toFixed(decimalEuro));
            const netAmInE: number = (parseFloat(newAmount) / tokenForFiat) - percInE - fixedFeeInEuro
            setNetAmountInEuro(netAmInE.toFixed(decimalEuro))
            setErrorAmount(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSended(true);
        setWaiterView(true);
        setMainErrorMex(null);
        setMainResponseMex(null);

        if(parseFloat(amount) < parseFloat(minAmountInTokens))
        {
            setMainErrorMex(t("L'importo di riscatto è troppo basso."))
            return;
        }

        if(parseFloat(amount) > parseFloat(maxAmountInTokens))
        {
            setMainErrorMex(t("L'importo di riscatto è troppo alto."))
            return;
        }


        if(!errorAmount && isChecked )
        {
            const newReq:IRedeemRequestModel = {
                amount: parseFloat(amount),
                iban: iban,
                ibanOwner: receiverName,
                bicSwift: bic,
                acceptTerms: isChecked
            }
            try{
                // richiesta al db
                const redeem = await redeemRequest(newReq);
                setAmount("");
                setReceiverName("");
                setIban("");
                setBic("");
                setMainResponseMex(t("La richiesta di riscatto è stata inserita correttamente, e ha codice di riferimento: ") + redeem.refCode)
                dashboard()
                    .then(resp => {
                        setAmountAvaible(resp.balance)
                    })
                    .catch(error => {
                        // initLoad.current = false
                        console.log(error)
                    })
                
            }catch(e:any){
                setMainErrorMex(e.data || e.message)
                setIsSended(false);
            }
            setWaiterView(false);
            setIsSended(false);
        }
        setStatus(!status); // reload delle ultime transazioni
    };

    return (
        <Box sx={{width:'100%', overflow: 'hidden', maxWidth: '100%', fontFamily:'poppins'}}>
            <Paper sx={{ 
                width: '100%', 
                display: 'flex', 
                minHeight:'350px', 
                border: 1, 
                borderColor: 'transparent', 
                overflow: 'hidden',
                maxWidth: '100%', // Limita la larghezza massima del Paper
                boxSizing: 'border-box'
            }}>
                
                <Box sx={{ flexGrow: 1, p: 3, position: 'relative' }}>
                    {response && (
                        <IconButton
                            onClick={() => window.print()}
                            sx={{ position: 'absolute', top: 16, right: 16 }}
                        >
                            <PrintIcon />
                        </IconButton>
                    )}

                    <Card sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                        <CardContent 
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                //padding: '24px 0 0 0',
                                flexDirection: 'column',
                                maxWidth: '100%', // Limita il contenuto a non superare il suo contenitore
                                boxSizing: 'border-box',
                            }}
                        >
                            {/* <Typography variant="h4" gutterBottom  color={BLACK} >
                                {t("Riscatta ASSO")}
                            </Typography> */}

                            { !response &&
                                <FormCustom onSubmit={handleSubmit}>
                                    <Grid container spacing={2} justifyContent={'center'} sx={{maxWidth: '100%', boxSizing: 'border-box' }}>

                                        {/** MAIN CONTAINER */}
                                        <Grid item xs={10} sm={8} justifyItems='center'>
                                            { 
                                                !mainResponseMex ? <>

                                                    {/** SEZIONE SALDO ATTUALE */}
                                                    <Grid container xs={12} spacing={1} justifyContent='center' paddingBottom={6}>
                                                        <Grid item xs={12} sm='auto' alignContent='center'>
                                                            <Typography color={GREEN_MAIN} textAlign={'center'} sx={{fontSize:"0.9rem", fontWeight:'600'}}>
                                                                {t("Il tuo saldo attuale ASSO sul conto è di")} 
                                                            </Typography>
                                                            <Typography color={GREEN_MAIN} textAlign={'center'} sx={{fontSize:"1.1rem", fontWeight:'600'}}>
                                                                {amountAvaiable}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>

                                                    {/** SEZIONE AMOUNT */}
                                                    <Grid container xs={12} spacing={1} justifyContent='center' paddingBottom={8}>
                                                        <Grid item xs={12} alignContent='center' paddingBottom={5}>
                                                            <Typography 
                                                                sx={{
                                                                    fontSize:'12px',
                                                                }}
                                                            >
                                                                {t("Vuoi riscattare ASSO e convertirli in euro?")}
                                                            </Typography>
                                                            <Typography 
                                                                sx={{
                                                                    fontSize:'12px',
                                                                }}
                                                            >
                                                                {t("Inserisci qui sotto la quantità di ASSO che desideri riscattare")}
                                                            </Typography>
                                                        </Grid>
                                                        
                                                        <Grid item xs={12} alignContent='center' paddingBottom={0}>
                                                            <TextField
                                                                fullWidth
                                                                label={t("Quantità ASSO")}
                                                                name="amount"
                                                                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {(handleInputAmountChange(evt))}}
                                                                onClick={() => {
                                                                    setAmount('')
                                                                    setErrorAmount(false)
                                                                }}
                                                                onBlur={() => handleBlurAmount()}
                                                                value={amount}
                                                                error={errorAmount}
                                                                // disabled={}
                                                                helperText={
                                                                    errorAmount ? errorAmountMessage : ""
                                                                }
                                                                sx={{
                                                                    '& .MuiOutlinedInput-notchedOutline':{
                                                                        borderColor: GREEN_LOGO,
                                                                        color: GREEN_MAIN,
                                                                    },
                                                                    '& .MuiInputLabel-root':{
                                                                        top:-4,
                                                                        fontSize:'1rem',
                                                                        color: GREEN_MAIN,
                                                                    },
                                                                    '& .MuiInputLabel-root.Mui-focused':{
                                                                        color: GREEN_MAIN
                                                                    },
                                                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: GREEN_LOGO,
                                                                        color: GREEN_MAIN,
                                                                    },
                                                                    '& .MuiOutlinedInput-input': {
                                                                        padding: '12px 12px',
                                                                        lineHeight: 1.4, 
                                                                        color:"grey", fontSize:'1rem',  
                                                                    },
                                                                }}
                                                            />
                                                            
                                                        </Grid>

                                                        {amount && amount !== '' && 
                                                            <>
                                                                <Grid item xs={12}>
                                                                    <Grid container xs={12} spacing={0} color={GREEN_MAIN} fontSize='0.7rem'>
                                                                        <Grid item textAlign="left" xs={8}>{t("Totale")}:</Grid>
                                                                        <Grid item textAlign="right" xs={4}>{amountInEuro} {t("euro")}</Grid>
                                                                    </Grid>
                                                                    <Grid container xs={12} spacing={0} color={GREEN_MAIN} fontSize='0.7rem'>
                                                                        <Grid item textAlign="left" xs={8}>{t("Commissione fissa")}:</Grid>
                                                                        <Grid item textAlign="right" xs={4}>{fixedFeeInEuro.toFixed(settings.RedeemTokens.Decimals)} {t("euro")}</Grid>
                                                                    </Grid>
                                                                    <Grid container xs={12} spacing={0} color={GREEN_MAIN} fontSize='0.7rem'>
                                                                        <Grid item textAlign="left" xs={8}>{t("Commissione variabile")} ( {percentFee} % ):</Grid>
                                                                        <Grid item textAlign="right" xs={4}>{parseFloat(percentFeeInEuro).toFixed(settings.RedeemTokens.Decimals)} {t("euro")}</Grid>
                                                                    </Grid>
                                                                    <Grid container xs={12} spacing={0} color={GREEN_MAIN} fontWeight='bold' fontSize='0.7rem' paddingTop={1}>
                                                                        <Grid item textAlign="left" xs={8}>{t("Accredito netto")}:</Grid>
                                                                        <Grid item textAlign="right" xs={4}>{parseFloat(NetAmountInEuro).toFixed(2)} {t("euro")}</Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </> 
                                                        }
                                                    </Grid>

                                                    {/** SEZIONE IBAN - GRID */}
                                                    <IbanForm
                                                        iban={iban}
                                                        setIban={setIban}
                                                        errorIban={errorIban}
                                                        setErrorIban={setErrorIban}
                                                        bic={bic}
                                                        setBic={setBic}
                                                        errorBic={errorBic}
                                                        setErrorBic={setErrorBic}
                                                        receiverName={receiverName}
                                                        setReceiverName={setReceiverName}
                                                        errorReceiverName={errorReceiverName}
                                                        setErrorReceiverName={setErrorReceiverName}
                                                    />

                                                    <Grid item xs={12} alignContent='center' paddingBottom={5}>
                                                        <CheckBoxTermsAndConditions isChecked={isChecked} setIsChecked={setIsChecked}/>
                                                    </Grid>
                                                    
                                                    {/** SEZIONE BUTTON INVIA*/}
                                                    <Grid item xs={6} sm={3} alignContent='center' paddingBottom={5}>
                                                        <Button type="submit" variant="contained" disabled={isSended || amount === '' || errorAmount || !isChecked || errorIban || receiverName === '' || !receiverName } 
                                                            sx={{
                                                                color:WHITE, 
                                                                border:'0',
                                                                backgroundColor: GREEN_LOGO,
                                                                '&:hover':{
                                                                    backgroundColor:'darkgreen'
                                                                }
                                                            }}>
                                                            {t("Invia")}
                                                        </Button>
                                                    </Grid>
                                                </>
                                                :<Grid container xs={12} spacing={1} justifyContent='center' paddingTop={5}>
                                                    <Grid item xs={12} sm='auto' alignContent='center'>
                                                        <Typography color={GREEN_MAIN} textAlign={'center'} sx={{fontSize:"0.9rem", fontWeight:'600'}}>
                                                            {t("Richiesta inoltrata correttamente!")} 
                                                        </Typography>
                                                    </Grid>
                                                </Grid>    
                                            }
                                        </Grid>                                            
                                        {
                                            mainErrorMex && <Grid item xs={12} display="grid">
                                                <Typography fontSize="0.75rem" color="red" 
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3, // Limita il numero di righe del testo
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                        wordWrap: 'break-word',
                                                        maxWidth: '100%', // Impedisce l'uscita dal contenitore
                                                    }}
                                                >
                                                    {mainErrorMex}
                                                </Typography>
                                            </Grid>
                                        }

                                        {
                                            // mainResponseMex && <Grid item xs={12} display="grid" > {/** marginBottom={2} marginTop={2} */}
                                            //     <Typography fontSize="0.75rem" color={GREEN_MAIN} 
                                            //         sx={{
                                            //             display: '-webkit-box',
                                            //             WebkitLineClamp: 3, // Limita il numero di righe del testo
                                            //             WebkitBoxOrient: 'vertical',
                                            //             overflow: 'hidden',
                                            //             textOverflow: 'ellipsis',
                                            //             whiteSpace: 'normal',
                                            //             wordWrap: 'break-word',
                                            //             maxWidth: '100%', // Impedisce l'uscita dal contenitore
                                            //         }}
                                            //     >
                                            //         {mainResponseMex}
                                            //     </Typography>
                                            // </Grid>
                                        }
                                    </Grid>
                                </FormCustom>
                            }
                        </CardContent>
                    </Card>
                </Box>
            </Paper>
        </Box>
    );
};

export default RedeemForm;
